import { MkIcon } from "@/mk/MkIcon";
import { isValidElement } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { RowAction, TableColumn } from "./MkTable";
import { cellValue } from "./MkTable";

interface Props<T> {
    columns: TableColumn<T>[];
    rows: T[];
    rowKey(row: T, index: number): string | number;
    loading?: boolean;
    skeletonCount?: number;
    onCardClick?(row: T): void;
    emptyText?: string;
    emptyIcon?: string;
    emptyActionLabel?: string;
    onEmptyAction?(): void;
    extraFields?: TableColumn<T>[];
    accentOf?(row: T): string;
    maxLines?: number;
    /** the same row actions the table view carries, so a card is not a dead end */
    actions?: RowAction<T>[];
    className?: string;
}

function asText(value: ReactNode): string {
    return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

export function MkCardGrid<T>({ columns, rows, rowKey, loading, skeletonCount = 8, onCardClick,
                                emptyText, emptyIcon, emptyActionLabel, onEmptyAction,
                                extraFields = [], accentOf, maxLines = 5, actions = [],
                                className }: Props<T>) {
    const { t } = useTranslation();
    const titleCol = columns.find((column) => column.cardTitle) ?? columns[0];
    const isHtml = (column: TableColumn<T>, row: T) =>
        column.html || isValidElement(cellValue(column, row));
    const seen = new Set(columns.map((column) => column.key));
    const extra = extraFields.filter((column) => column.key && !seen.has(column.key));
    /** a click on a button must not also open the card */
    const stop = (event: ReactMouseEvent) => event.stopPropagation();
    const rowActions = (row: T) =>
        actions.filter((action) => action.visible ? action.visible(row) : true);

    return (
        <div className={`mk-cardgrid${className ? ` ${className}` : ""}`}>
            {loading ? (
                Array.from({ length: skeletonCount }, (_, index) => (
                    <div key={index} className="mk-cardgrid-card mk-cardgrid-skeleton">
                        <span className="mk-skeleton mk-skeleton-text" style={{ width: "60%" }} />
                        <span className="mk-skeleton mk-skeleton-text" />
                        <span className="mk-skeleton mk-skeleton-text" style={{ width: "80%" }} />
                    </div>
                ))
            ) : !rows.length ? (
                <div className="mk-cardgrid-empty">
                    <span className="mk-table-empty-icon">
                        <MkIcon name={emptyIcon || "fa-inbox"} />
                    </span>
                    <span className="mk-table-empty-text">{emptyText || t("mk.table.noRecords")}</span>
                    {emptyActionLabel && (
                        <button className="mk-table-empty-action" onClick={() => onEmptyAction?.()}>
                            <MkIcon name="fa-plus" />
                            <span>{emptyActionLabel}</span>
                        </button>
                    )}
                </div>
            ) : rows.map((row, index) => {
                const accent = accentOf ? accentOf(row) : "";
                const classes = ["mk-cardgrid-card", onCardClick ? "mk-cardgrid-click" : "",
                                 accent ? `mk-card-accent mk-card-${accent}` : ""]
                    .filter(Boolean).join(" ");
                const title = titleCol ? asText(cellValue(titleCol, row)) : "";
                const badges = columns.filter((column) => column !== titleCol && isHtml(column, row));
                const body = [...columns.filter((column) => column !== titleCol && !isHtml(column, row)),
                              ...extra].slice(0, maxLines);
                return (
                    <div key={rowKey(row, index)} className={classes}
                         onClick={() => onCardClick?.(row)}>
                        <div className="mk-cardgrid-head">
                            <span className="mk-cardgrid-title" title={title || "—"}>{title || "—"}</span>
                            <span className="mk-cardgrid-badges">
                                {badges.map((column) => (
                                    <span key={column.key} className="mk-cardgrid-badge">
                                        {cellValue(column, row)}
                                    </span>
                                ))}
                            </span>
                        </div>
                        {rowActions(row).length ? (
                            <div className="mk-table-actions mk-cardgrid-actions">
                                {rowActions(row).map((action, actionIndex) => (
                                    <button key={actionIndex}
                                            className={`mk-table-action${action.variant ? ` mk-table-action-${action.variant}` : ""}`}
                                            title={action.title}
                                            onClick={(event) => {
                                                stop(event);
                                                action.run(row);
                                            }}>
                                        <MkIcon name={action.icon} />
                                    </button>
                                ))}
                            </div>
                        ) : null}
                        <div className="mk-cardgrid-body">
                            {body.map((column) => {
                                const value = asText(cellValue(column, row));
                                return (
                                    <div key={column.key} className="mk-cardgrid-line">
                                        <span className="mk-cardgrid-label">{column.label}</span>
                                        <span className={`mk-cardgrid-value${value === "" ? " mk-cell-empty" : ""}`}
                                              title={value === "" ? undefined : value}>
                                            {value === "" ? "—" : value}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
